// src/runtime/EnterpriseRuntime/workspace/types.ts
// ============================================
// ERP 工作台类型定义（Sprint 2 Task 3）
//
// 职责：
//   1. 定义 Vault 12 大业务模块枚举（CRM/SKU/SOP/Meeting/Project 等）
//   2. 定义统一数据模型：VaultItem / FinanceRecord / ProjectTask / MeetingNote / DailyReport
//   3. 定义工作台查询/命令枚举（WorkspaceQuery / WorkspaceCommand）
//   4. 定义统一返回类型与统计聚合类型
//
// 设计原则：
//   - 所有 Markdown 必须有 frontmatter
//   - VaultItem 是统一抽象，所有目录共用
//   - 财务/项目/会议/日报是 VaultItem 的领域特化
// ============================================

// ============================================
// Vault 业务模块枚举
// ============================================

/**
 * Vault 业务模块（12 大领域）
 *
 * 对应真实 Vault D:/Obsidian/小舍AI_中央大脑库/XEAOS/ 下的 12 个子目录：
 *   CRM       - 客户关系管理
 *   SKU       - 产品/服务 SKU
 *   SOP       - 标准操作流程
 *   Meeting   - 会议纪要
 *   Project   - 项目档案
 *   Research  - 调研报告
 *   Daily     - 日报/周报
 *   Workflow  - 工作流案例
 *   Prompt    - Prompt 模板
 *   Memory    - AI 记忆
 *   Finance   - 财务记录
 *   Team      - 团队成员
 */
export enum VaultSection {
  CRM = 'CRM',
  SKU = 'SKU',
  SOP = 'SOP',
  Meeting = 'Meeting',
  Project = 'Project',
  Research = 'Research',
  Daily = 'Daily',
  Workflow = 'Workflow',
  Prompt = 'Prompt',
  Memory = 'Memory',
  Finance = 'Finance',
  Team = 'Team',
}

// ============================================
// VaultItem - 统一条目模型
// ============================================

/**
 * Vault 统一条目
 *
 * 所有 12 个目录的 Markdown 文件都映射为 VaultItem
 * 通过 section 字段区分所属业务模块
 */
export interface VaultItem {
  /** 条目 ID（文件名去 .md） */
  id: string;
  /** 所属业务模块 */
  section: VaultSection;
  /** 标题（frontmatter.title 或文件名） */
  title: string;
  /** 文件绝对路径 */
  filePath: string;
  /** YAML frontmatter 元数据 */
  frontmatter: Record<string, unknown>;
  /** 正文（去除 frontmatter 后的内容） */
  body: string;
  /** 标签列表（frontmatter.tags 或正文 #tag） */
  tags: string[];
  /** 创建时间（ISO） */
  createdAt: string;
  /** 更新时间（ISO） */
  updatedAt: string;
  /** 创建人（可选） */
  createdBy?: string;
}

// ============================================
// 财务管理
// ============================================

/**
 * 财务记录
 *
 * 存储路径：Finance/{YYYY}-{MM}.md（按月聚合）
 * 单条记录以 frontmatter + 表格行形式写入
 */
export interface FinanceRecord {
  /** 记录 ID */
  id: string;
  /** 类型：收入 / 支出 */
  type: 'income' | 'expense';
  /** 分类（如：销售收入 / 广告投放 / 人力成本） */
  category: string;
  /** 金额（人民币，元） */
  amount: number;
  /** 发生日期（YYYY-MM-DD） */
  date: string;
  /** 关联项目（可选） */
  relatedProject?: string;
  /** 关联客户（可选） */
  relatedCustomer?: string;
  /** 描述说明 */
  description: string;
  /** 所属文件路径 */
  filePath: string;
}

// ============================================
// 项目任务管理
// ============================================

/**
 * 项目任务
 *
 * 存储路径：Project/{项目名}.md 文件内的任务清单
 * 通过 frontmatter + 任务列表维护
 */
export interface ProjectTask {
  /** 任务 ID */
  id: string;
  /** 所属项目 ID */
  projectId: string;
  /** 任务标题 */
  title: string;
  /** 负责人（可选） */
  assignee?: string;
  /** 任务状态 */
  status: 'todo' | 'in_progress' | 'review' | 'done' | 'blocked';
  /** 优先级 */
  priority: 'high' | 'medium' | 'low';
  /** 截止日期（YYYY-MM-DD，可选） */
  dueDate?: string;
  /** 任务描述 */
  description: string;
}

// ============================================
// 会议管理
// ============================================

/**
 * 会议待办事项
 */
export interface MeetingActionItem {
  /** 待办内容 */
  task: string;
  /** 负责人 */
  owner: string;
  /** 截止日期（可选） */
  dueDate?: string;
  /** 状态：待办 / 完成 */
  status: 'pending' | 'done';
}

/**
 * 会议纪要
 *
 * 存储路径：Meeting/{YYYY-MM-DD} {主题}.md
 */
export interface MeetingNote {
  /** 会议 ID（文件名去 .md） */
  id: string;
  /** 会议主题 */
  title: string;
  /** 会议日期（YYYY-MM-DD） */
  date: string;
  /** 参会人员 */
  participants: string[];
  /** 议程列表 */
  agenda: string[];
  /** 决策列表 */
  decisions: string[];
  /** 待办事项 */
  actionItems: MeetingActionItem[];
  /** 文件路径 */
  filePath: string;
}

// ============================================
// 日报/周报
// ============================================

/**
 * 日报
 *
 * 存储路径：Daily/{YYYY-MM-DD}.md
 */
export interface DailyReport {
  /** 日报 ID（文件名去 .md） */
  id: string;
  /** 日期（YYYY-MM-DD） */
  date: string;
  /** 作者 */
  author: string;
  /** 当日完成事项 */
  accomplishments: string[];
  /** 明日计划 */
  plans: string[];
  /** 阻碍/问题 */
  blockers: string[];
  /** 关键指标（如：客户数、收入、产出等） */
  metrics: Record<string, number>;
  /** 文件路径 */
  filePath: string;
}

// ============================================
// 查询/命令枚举
// ============================================

/**
 * 工作台查询类型
 */
export enum WorkspaceQuery {
  /** 列出某模块条目 */
  ListItems = 'list_items',
  /** 获取单条目 */
  GetItem = 'get_item',
  /** 跨目录搜索 */
  Search = 'search',
  /** 查询财务记录 */
  GetFinance = 'get_finance',
  /** 查询项目任务 */
  GetProjectTasks = 'get_project_tasks',
  /** 查询会议列表 */
  GetMeetings = 'get_meetings',
  /** 查询日报列表 */
  GetDailyReports = 'get_daily_reports',
  /** 获取工作台统计 */
  GetStats = 'get_stats',
}

/**
 * 工作台命令类型
 */
export enum WorkspaceCommand {
  /** 创建条目 */
  CreateItem = 'create_item',
  /** 更新条目 */
  UpdateItem = 'update_item',
  /** 删除条目 */
  DeleteItem = 'delete_item',
  /** 创建财务记录 */
  CreateFinance = 'create_finance',
  /** 创建项目任务 */
  CreateTask = 'create_task',
  /** 更新项目任务 */
  UpdateTask = 'update_task',
  /** 创建会议 */
  CreateMeeting = 'create_meeting',
  /** 创建日报 */
  CreateDailyReport = 'create_daily_report',
  /** 自动生成日报 */
  GenerateDailyReport = 'generate_daily_report',
}

// ============================================
// 统一返回类型
// ============================================

/**
 * 工作台统一返回类型
 */
export interface WorkspaceResult<T = unknown> {
  /** 是否成功 */
  success: boolean;
  /** 返回数据 */
  data?: T;
  /** 错误信息 */
  error?: string;
  /** 时间戳（ISO） */
  timestamp: string;
}

// ============================================
// 统计聚合
// ============================================

/**
 * 工作台统计
 *
 * 用于仪表盘展示
 */
export interface WorkspaceStats {
  /** 总条目数 */
  totalItems: number;
  /** 按模块统计 */
  bySection: Record<VaultSection, number>;
  /** 财务汇总 */
  financeSummary: {
    /** 总收入 */
    totalIncome: number;
    /** 总支出 */
    totalExpense: number;
    /** 利润 */
    profit: number;
    /** 按分类汇总 */
    byCategory: Record<string, number>;
  };
  /** 活跃项目数 */
  activeProjects: number;
  /** 待办任务数 */
  pendingTasks: number;
  /** 即将召开的会议数 */
  upcomingMeetings: number;
}
